import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ShieldCheck, Wrench, Headphones, UserCheck, Zap, ArrowRight, Star } from "lucide-react";
import PageHeader from "../components/PageHeader";
import SEO from "../components/SEO";
import { itSupportPlans, itSupportBenefits, siteInfo } from "../mock";
import { useBooking } from "../context/BookingContext";

const iconMap = { ShieldCheck, Wrench, Headphones, UserCheck, Zap };

const fmt = (n) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", minimumFractionDigits: n % 1 === 0 ? 0 : 2 }).format(n);

const ITSupport = () => {
  const booking = useBooking();
  return (
    <>
      <SEO
        title="IT Support-abonnement | Vanaf €15 per maand — Computheer"
        description="Nooit meer stress om computerproblemen. Bitdefender, onderhoud en onbeperkte hulp op afstand vanaf €15 per maand. Voor particulieren en bedrijven."
      />
      <PageHeader
        eyebrow="IT Support"
        title="Altijd een werkende computer."
        subtitle="Nooit meer gedoe met virussen, trage computers of updates. Eén vast aanspreekpunt voor al uw IT — vanaf €15 per maand."
      />

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-14">
            <p className="text-sm tracking-[0.3em] uppercase text-neutral-500 mb-3">— Wat u krijgt</p>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 leading-tight">
              Vijf voordelen die u <span className="italic font-light">meteen</span> merkt.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {itSupportBenefits.map((b) => {
              const Icon = iconMap[b.icon] || ShieldCheck;
              return (
                <div key={b.title} className="border border-neutral-200 p-6 hover:border-amber-500 hover:shadow-md transition-all bg-white">
                  <div className="w-12 h-12 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mb-4">
                    <Icon size={20} className="text-amber-700"/>
                  </div>
                  <h3 className="text-base font-bold text-neutral-900 mb-2">{b.title}</h3>
                  <p className="text-sm text-neutral-600 leading-relaxed">{b.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-sm tracking-[0.3em] uppercase text-neutral-500 mb-3">— Kies uw pakket</p>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 leading-tight">
              Transparante prijzen,
              <br/>
              <span className="italic font-light">geen kleine lettertjes.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-[980px] mx-auto items-stretch">
            {itSupportPlans.map((p) => (
              <div
                key={p.id}
                data-testid={`its-plan-${p.id}`}
                className={`relative flex flex-col bg-neutral-900 text-white p-8 md:p-10 shadow-2xl border ${
                  p.business ? "border-amber-500" : "border-neutral-800"
                }`}
              >
                {p.business && (
                  <div className="absolute -top-3 right-6 bg-amber-500 text-neutral-900 text-[10px] font-bold tracking-widest uppercase px-3 py-1">
                    Voor bedrijven
                  </div>
                )}
                <div className="mb-6">
                  <p className="text-[11px] uppercase tracking-widest text-neutral-400 mb-1">{p.tagline}</p>
                  <h3 className="text-xl md:text-2xl font-bold text-white">{p.name}</h3>
                </div>
                <div className="mb-7">
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold text-white">{fmt(p.priceMonthly)}</span>
                    <span className="text-sm text-neutral-400">/ maand</span>
                  </div>
                  <p className="text-sm text-neutral-400 mt-1.5">
                    of <span className="text-neutral-200 font-medium">{fmt(p.priceYearly)} / jaar</span>
                    {p.yearlyNote && <span> — <span className={p.business ? "text-neutral-400" : "text-amber-300"}>{p.yearlyNote}</span></span>}
                  </p>
                  {p.deviceLimit && (
                    <p className="text-xs text-neutral-500 mt-2 italic">{p.deviceLimit}</p>
                  )}
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-[15px] text-neutral-100">
                      <CheckCircle2 size={18} className={`shrink-0 mt-0.5 ${p.business ? "text-amber-400" : "text-emerald-400"}`}/>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={booking.open}
                  className={`w-full inline-flex items-center justify-center gap-2 py-3.5 text-sm font-medium transition-colors ${
                    p.business ? "bg-amber-500 hover:bg-amber-400 text-neutral-900" : "bg-white hover:bg-neutral-100 text-neutral-900"
                  }`}
                  data-testid={`its-plan-cta-${p.id}`}
                >
                  {p.business ? "Vraag offerte aan" : "Kies dit pakket"}
                  <ArrowRight size={15}/>
                </button>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-neutral-500 mt-8">
            Maandelijks opzegbaar · Geen opstartkosten · Jaarabonnement met 2 maanden gratis
          </p>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 bg-neutral-900 text-white">
        <div className="max-w-2xl mx-auto px-5 md:px-10 text-center">
          <div className="flex justify-center gap-1 mb-5">
            {[...Array(5)].map((_, i) => <Star key={i} size={18} className="fill-amber-400 text-amber-400"/>)}
          </div>
          <p className="text-2xl md:text-3xl leading-relaxed font-light italic mb-6">
            &ldquo;Sinds we het IT Support-abonnement hebben afgesloten hoeven we nergens meer naar om te kijken. Alles wordt bijgehouden, virussen zijn er niet meer en bij de kleinste vraag staat Computheer meteen klaar. Een enorme geruststelling.&rdquo;
          </p>
          <p className="text-sm text-neutral-400 uppercase tracking-widest">— Familie De Bruin · IT Support klant sinds 2024</p>
        </div>
      </section>

      {/* FAQ mini */}
      <section className="py-20 bg-white">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <p className="text-sm tracking-[0.3em] uppercase text-neutral-500 mb-3">— Veelgestelde vragen</p>
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 leading-tight mb-8">
            Alles wat u wilt weten <span className="italic font-light">vóór</span> u start.
          </h2>
          <div className="divide-y divide-neutral-200 border-t border-b border-neutral-200">
            {[
              { q: "Wat kost het abonnement?", a: "Voor particulieren €15 per maand (of €150 per jaar met 2 maanden gratis). Voor zakelijke klanten €25 per maand (of €250 per jaar)." },
              { q: "Hoe snel word ik geholpen?", a: "Abonnees krijgen voorrang. In de meeste gevallen bent u binnen 24 uur geholpen — via hulp op afstand vaak zelfs binnen een uur." },
              { q: "Wat gebeurt er met mijn oude antivirus?", a: "Wij verwijderen die en installeren gratis Bitdefender Total Security. Uw bestaande licentie mag u afzeggen of laten uitlopen." },
              { q: "Kan ik maandelijks opzeggen?", a: "Het maandabonnement is maandelijks opzegbaar. Het jaarabonnement loopt telkens één jaar en wordt stilzwijgend verlengd tenzij u uiterlijk één maand voor de einddatum opzegt." },
              { q: "Werkt het ook voor Mac?", a: "Absoluut. Windows, Mac én mobiele apparaten worden allemaal ondersteund." },
            ].map((it, i) => (
              <details key={i} className="group py-5">
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="text-lg font-medium text-neutral-900">{it.q}</span>
                  <span className="text-neutral-400 group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-neutral-600 leading-relaxed">{it.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-white">
        <div className="max-w-[900px] mx-auto px-5 md:px-10 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 leading-tight mb-4">
            Liever geen onverwachte computerproblemen?
          </h2>
          <p className="text-lg text-neutral-600 mb-8">
            Kies voor Computheer IT Support. Vanaf <strong>€15 per maand</strong> (of <strong>€150 per jaar</strong>).
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={booking.open}
              className="inline-flex items-center justify-center gap-2 bg-neutral-900 hover:bg-black text-white px-7 py-4 text-[15px] font-medium">
              Plan een gratis intake
              <ArrowRight size={16}/>
            </button>
            <a href={`tel:${siteInfo.phoneRaw}`}
              className="inline-flex items-center justify-center gap-2 border border-neutral-300 hover:border-neutral-900 text-neutral-900 px-7 py-4 text-[15px] font-medium">
              Bel {siteInfo.phone}
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default ITSupport;
